// ─────────────────────────────────────────────────────────────
// Amortization & debt strategy math
//
// Pure functions — no React, no async, no side effects.
// All money is represented as numbers (dollars). All rates are annual
// percentages (e.g. 19.99 means 19.99% APR).
// ─────────────────────────────────────────────────────────────

export function calculatePayoff(balance, annualRate, monthlyPayment) {
  const monthlyRate = annualRate / 100 / 12
  const minViablePayment = balance * monthlyRate

  if (monthlyPayment <= minViablePayment) {
    return {
      months: Infinity,
      totalInterest: Infinity,
      totalPaid: Infinity,
      schedule: [],
      willPayOff: false,
    }
  }

  const schedule = []
  let remaining = balance
  let totalInterest = 0
  let month = 0
  const MAX_MONTHS = 1200

  while (remaining > 0.01 && month < MAX_MONTHS) {
    month++
    const interestThisMonth = remaining * monthlyRate
    let principalThisMonth = monthlyPayment - interestThisMonth
    let payment = monthlyPayment

    if (principalThisMonth >= remaining) {
      principalThisMonth = remaining
      payment = remaining + interestThisMonth
    }

    remaining -= principalThisMonth
    totalInterest += interestThisMonth

    schedule.push({
      month,
      balance: Math.max(0, remaining),
      interest: interestThisMonth,
      principal: principalThisMonth,
      payment,
    })
  }

  return {
    months: month,
    totalInterest,
    totalPaid: balance + totalInterest,
    schedule,
    willPayOff: month < MAX_MONTHS,
  }
}

export function runStrategy(debts, totalMonthlyBudget, strategy = 'avalanche', overrides = {}) {
  const working = debts.map(d => ({
    id: d.id,
    name: d.name,
    balance: d.balance,
    monthlyRate: d.rate / 100 / 12,
    minPayment: d.minPayment,
  }))

  const totalMin = working.reduce((s, d) => s + d.minPayment, 0)
  if (totalMonthlyBudget < totalMin) {
    return {
      months: Infinity,
      totalInterest: Infinity,
      totalPaid: Infinity,
      schedule: [],
      willPayOff: false,
      perDebtPayoff: {},
      error: `Budget ${totalMonthlyBudget} below sum of minimum payments ${totalMin}`,
    }
  }

  const sortByStrategy = (arr) => {
    if (strategy === 'avalanche') {
      return [...arr].sort((a, b) => b.monthlyRate - a.monthlyRate)
    }
    return [...arr].sort((a, b) => a.balance - b.balance)
  }

  const schedule = []
  const perDebtPayoff = {}
  let totalInterest = 0
  // totalPaid is an explicit accumulator (triage item #3): it sums the ACTUAL
  // payment applied each month across all debts, independent of the mutated
  // working balances. Over the full run this equals starting_total_balance +
  // total_interest (every dollar of principal plus every dollar of interest
  // actually paid). Computing it from the mutated `working` balances — which
  // are driven to ~0 — would wrongly return just the interest.
  let totalPaid = 0
  let month = 0
  const MAX_MONTHS = 1200

  while (working.some(d => d.balance > 0.01) && month < MAX_MONTHS) {
    month++
    const monthRecord = { month, totalBalance: 0, totalInterest: 0, totalPrincipal: 0, payments: [] }
    let extraThisMonth = totalMonthlyBudget

    // Step 1: minimums + interest
    for (const d of working) {
      if (d.balance <= 0.01) {
        monthRecord.payments.push({ debtId: d.id, balance: 0, payment: 0, interest: 0, principal: 0 })
        continue
      }

      const interest = d.balance * d.monthlyRate
      const minP = Math.min(d.minPayment, d.balance + interest)
      d.balance = d.balance + interest - minP

      const principal = minP - interest
      extraThisMonth -= minP
      totalInterest += interest

      monthRecord.payments.push({
        debtId: d.id,
        balance: Math.max(0, d.balance),
        payment: minP,
        interest,
        principal: Math.max(0, principal),
      })
      monthRecord.totalInterest += interest
      monthRecord.totalPrincipal += principal
    }

    // Step 2: apply user-overridden extras for this month
    const monthOverrides = overrides[month] || {}
    for (const [debtId, overrideAmount] of Object.entries(monthOverrides)) {
      const d = working.find(w => w.id === debtId)
      if (!d || d.balance <= 0.01) continue
      const extra = parseFloat(overrideAmount) || 0
      if (extra <= 0) continue
      const apply = Math.min(extra, d.balance, extraThisMonth)
      if (apply <= 0) continue
      d.balance -= apply
      extraThisMonth -= apply
      const rec = monthRecord.payments.find(p => p.debtId === d.id)
      if (rec) {
        rec.payment += apply
        rec.principal += apply
        rec.balance = Math.max(0, d.balance)
        monthRecord.totalPrincipal += apply
      }
    }

    // Step 3: throw remaining extra at the priority debt
    if (extraThisMonth > 0.01) {
      const sorted = sortByStrategy(working.filter(d => d.balance > 0.01))
      for (const priorityDebt of sorted) {
        if (extraThisMonth <= 0.01) break
        const apply = Math.min(extraThisMonth, priorityDebt.balance)
        priorityDebt.balance -= apply
        extraThisMonth -= apply

        const rec = monthRecord.payments.find(p => p.debtId === priorityDebt.id)
        if (rec) {
          rec.payment += apply
          rec.principal += apply
          rec.balance = Math.max(0, priorityDebt.balance)
          monthRecord.totalPrincipal += apply
        }
      }
    }

    for (const d of working) {
      if (d.balance <= 0.01 && !perDebtPayoff[d.id]) {
        perDebtPayoff[d.id] = month
      }
    }

    // Accumulate every dollar actually paid this month (minimums + all extras).
    totalPaid += monthRecord.payments.reduce((s, p) => s + p.payment, 0)

    monthRecord.totalBalance = working.reduce((s, d) => s + Math.max(0, d.balance), 0)
    schedule.push(monthRecord)
  }

  return {
    months: month,
    totalInterest,
    totalPaid,
    schedule,
    willPayOff: month < MAX_MONTHS,
    perDebtPayoff,
  }
}

// Simple daily interest accrued on a tracked-rate debt since it was last
// accrued (Chunk 6). balance × (annualRatePct/100 / 365) × days_elapsed — no
// compounding within the window (users open Debts at least monthly, so this is
// accurate enough). Returns { accrued, days }. `annualRatePct` is a percentage
// (e.g. 18.99), matching how debts.interest_rate is stored.
//
// Callers are responsible for the simple-mode / rate>0 gate; this returns
// { accrued: 0 } for a zero/negative balance, zero rate, or missing/future
// last-accrual timestamp, so it's safe to call unconditionally.
export function computeAccruedInterest(balance, annualRatePct, lastAccrualAt, now = new Date()) {
  const b = parseFloat(balance || 0)
  const rate = parseFloat(annualRatePct || 0)
  if (!(b > 0) || !(rate > 0) || !lastAccrualAt) return { accrued: 0, days: 0 }
  const last = new Date(lastAccrualAt)
  if (isNaN(last.getTime())) return { accrued: 0, days: 0 }
  const days = (now.getTime() - last.getTime()) / (1000 * 60 * 60 * 24)
  if (!(days > 0)) return { accrued: 0, days: 0 }
  return { accrued: b * (rate / 100 / 365) * days, days }
}

export function formatMonths(months) {
  if (!isFinite(months)) return 'never'
  if (months === 0) return '0 mos'
  const years = Math.floor(months / 12)
  const remaining = months % 12
  if (years === 0) return `${remaining} mo${remaining === 1 ? '' : 's'}`
  if (remaining === 0) return `${years} yr${years === 1 ? '' : 's'}`
  return `${years} yr${years === 1 ? '' : 's'} ${remaining} mo${remaining === 1 ? '' : 's'}`
}

export function monthsFromNow(months, fromDate = new Date()) {
  const d = new Date(fromDate)
  d.setMonth(d.getMonth() + months)
  return d
}